/**
 * Utilities for building threshold-related parameters for data queries.
 */

import type {
  ApiThresholdCatalogSource,
  ApplyThresholdOptions,
  ApplyThresholds,
  ThresholdDomainParams,
  ThresholdRequest,
} from '@highimpact-frontend/api-types';
import type { ThresholdSupportedPlotType } from './catalog-query';
import type {
  ThresholdVariableModel,
  UIThresholdModel,
} from './catalog-transform';

export type {
  ApplyThresholdOptions,
  ApplyThresholds,
  ThresholdDomainParams,
  ThresholdRequest,
} from '@highimpact-frontend/api-types';

// -- type definitions --

export interface ThresholdVariableColumnOverrides {
  [variableName: string]: string;
}

export interface ThresholdDomainOverrides {
  [variableName: string]: ThresholdDomainParams;
}

/**
 * Optional knobs for adapting threshold variable mappings and request payloads
 * to the conventions of each widget or future threshold family.
 */
export interface ThresholdDataQueryBuilderOptions {
  /**
   * Overrides the default time-column name used by time-aware widgets.
   *
   * @example
   * 'timestamp'
   */
  timeColumnName?: string;

  /**
   * Overrides the default windrose column names for polar dimensions and the
   * measured value.
   *
   * @example
   * {
   *   theta: 'direction_angle_deg',
   *   r: 'speed_bin_center',
   *   value: 'percent_time',
   * }
   */
  windroseColumns?: Partial<Record<'theta' | 'r' | 'value', string>>;

  /**
   * Maps a source binding key like mp:101:mt:39 to a concrete data-column name.
   *
   * @example
   * {
   *   'mp:101:mt:39': 'temperature_surface',
   * }
   */
  sourceColumnOverrides?: Readonly<Record<string, string>>;

  /**
   * Provides extra dimension aliases that can be matched against threshold
   * variable names or dimension references.
   *
   * @example
   * {
   *   value: 'percent_time',
   *   theta: 'direction_angle_deg',
   * }
   */
  additionalDimensionMappings?: Readonly<Record<string, string>>;

  /**
   * Applies explicit per-threshold variable-to-column overrides when automatic
   * resolution is not sufficient.
   *
   * @example
   * {
   *   7001: {
   *     value: 'custom_value_column',
   *   },
   * }
   */
  variableOverrides?: Readonly<
    Record<number, ThresholdVariableColumnOverrides>
  >;

  /**
   * Adds typed threshold options to every generated threshold request.
   *
   * @example
   * {
   *   return_breach_points_limit: 250,
   * }
   */
  extraRequestOptions?: Readonly<ApplyThresholdOptions>;

  /**
   * Adds typed threshold options for one specific threshold assignment only.
   *
   * @example
   * {
   *   7001: {
   *     return_breach_points_limit: 500,
   *   },
   * }
   */
  perThresholdOptions?: Readonly<Record<number, ApplyThresholdOptions>>;

  /**
   * Adds default domain overrides to every generated threshold request.
   *
   * @example
   * {
   *   value: { min: 0, max: 100 },
   * }
   */
  extraRequestDomains?: Readonly<ThresholdDomainOverrides>;

  /**
   * Adds domain overrides for one specific threshold assignment only.
   *
   * @example
   * {
   *   7001: {
   *     value: { min: 0, max: 100, sample_count: 50 },
   *   },
   * }
   */
  perThresholdDomains?: Readonly<Record<number, ThresholdDomainOverrides>>;

  /**
   * Sets the maximum number of breach points returned by the backend per
   * threshold request.
   *
   * @example
   * 500
   */
  returnBreachPointsLimit?: number;
}

export interface ThresholdDataQueryBuilderInput {
  selectedThresholds: UIThresholdModel[];
  selectedSources: ApiThresholdCatalogSource[];
  plotType: string;
  options?: ThresholdDataQueryBuilderOptions;
}

export type ThresholdRequestPayload = ThresholdRequest;
export type ThresholdDataQueryParameters = ApplyThresholds;

type VariableMappingContext = {
  threshold: UIThresholdModel;
  selectedSources: ApiThresholdCatalogSource[];
  plotType: string;
  options: ThresholdDataQueryBuilderOptions | undefined;
};

// -- constants --

const DEFAULT_TIME_COLUMN_NAME = 'Temps';
const DEFAULT_WINDROSE_COLUMNS = {
  theta: 'wind_direction',
  r: 'wind_speed',
  value: 'frequency',
} as const;

const SUPPORTED_REQUEST_PLOT_TYPES: ThresholdSupportedPlotType[] = [
  'timeseries',
  'scatter',
  'ombrothermic',
  'windrose',
  'bar',
];

// -- functions --

function isSupportedRequestPlotType(
  plotType: string
): plotType is ThresholdSupportedPlotType {
  return (SUPPORTED_REQUEST_PLOT_TYPES as string[]).includes(plotType);
}

function assertValidSourceBinding(source: ApiThresholdCatalogSource): void {
  if (!Number.isFinite(source.measure_point_id)) {
    throw new Error(
      'Each selected source must define a valid measure_point_id'
    );
  }

  if (!Number.isFinite(source.measure_type_id)) {
    throw new Error('Each selected source must define a valid measure_type_id');
  }
}

function normalizeKey(value: string): string {
  return value.trim().toLowerCase();
}

function isSameSourceBinding(
  left: ApiThresholdCatalogSource,
  right: ApiThresholdCatalogSource
): boolean {
  return (
    left.measure_point_id === right.measure_point_id &&
    left.measure_type_id === right.measure_type_id
  );
}

function getSourceBindingKey(source: ApiThresholdCatalogSource): string {
  return `mp:${source.measure_point_id}:mt:${source.measure_type_id}`;
}

function buildSourceColumnName(
  source: ApiThresholdCatalogSource,
  options?: ThresholdDataQueryBuilderOptions
): string {
  const defaultKey = getSourceBindingKey(source);
  return options?.sourceColumnOverrides?.[defaultKey] ?? defaultKey;
}

function inferVariableModel(variableName: string): ThresholdVariableModel {
  const normalizedName = normalizeKey(variableName);

  if (normalizedName === 't' || normalizedName === 'time') {
    return {
      variableName,
      dataType: 'datetime',
      role: 'time',
      dimensionRef: 'time',
      required: true,
    };
  }

  if (normalizedName === 'value' || normalizedName === 'y') {
    return {
      variableName,
      dataType: 'number',
      role: 'dependent',
      dimensionRef: normalizedName,
      required: true,
    };
  }

  return {
    variableName,
    dataType: 'number',
    role: 'independent',
    dimensionRef: normalizedName,
    required: true,
  };
}

function getThresholdVariables(
  threshold: UIThresholdModel
): ThresholdVariableModel[] {
  if (threshold.variableDetails.length > 0) {
    return [...threshold.variableDetails];
  }

  return threshold.variables.map((variableName) =>
    inferVariableModel(variableName)
  );
}

function getRelevantBindings(
  threshold: UIThresholdModel,
  selectedSources: ApiThresholdCatalogSource[]
): ApiThresholdCatalogSource[] {
  const thresholdBindings = threshold.sourceBindings.filter(
    (source) =>
      Number.isFinite(source.measure_point_id) &&
      Number.isFinite(source.measure_type_id)
  );

  if (thresholdBindings.length === 0) {
    return [...selectedSources];
  }

  const matchingBindings = selectedSources.filter((selectedSource) =>
    thresholdBindings.some((binding) =>
      isSameSourceBinding(binding, selectedSource)
    )
  );

  return matchingBindings.length > 0
    ? matchingBindings
    : [...thresholdBindings];
}

function getPrimaryValueBinding(
  context: VariableMappingContext
): ApiThresholdCatalogSource | null {
  const relevantBindings = getRelevantBindings(
    context.threshold,
    context.selectedSources
  );

  return relevantBindings[0] ?? context.selectedSources[0] ?? null;
}

function getScatterBindings(context: VariableMappingContext): {
  xBinding: ApiThresholdCatalogSource | null;
  yBinding: ApiThresholdCatalogSource | null;
} {
  const relevantBindings = getRelevantBindings(
    context.threshold,
    context.selectedSources
  );
  const xBinding =
    context.selectedSources.at(-1) ?? relevantBindings.at(-1) ?? null;

  const yBinding =
    relevantBindings.find(
      (binding) => xBinding === null || !isSameSourceBinding(binding, xBinding)
    ) ??
    context.selectedSources.find(
      (binding) => xBinding === null || !isSameSourceBinding(binding, xBinding)
    ) ??
    relevantBindings[0] ??
    context.selectedSources[0] ??
    null;

  return { xBinding, yBinding };
}

function buildDefaultDimensionMappings(
  context: VariableMappingContext
): Record<string, string> {
  const timeColumnName =
    context.options?.timeColumnName ?? DEFAULT_TIME_COLUMN_NAME;

  switch (context.plotType) {
    case 'timeseries':
    case 'ombrothermic': {
      const primaryBinding = getPrimaryValueBinding(context);
      const valueColumn = primaryBinding
        ? buildSourceColumnName(primaryBinding, context.options)
        : 'value';

      return {
        t: timeColumnName,
        time: timeColumnName,
        x: timeColumnName,
        value: valueColumn,
        y: valueColumn,
      };
    }

    case 'scatter': {
      const { xBinding, yBinding } = getScatterBindings(context);
      const xColumn = xBinding
        ? buildSourceColumnName(xBinding, context.options)
        : 'x';
      const yColumn = yBinding
        ? buildSourceColumnName(yBinding, context.options)
        : 'y';

      return {
        x: xColumn,
        y: yColumn,
        value: yColumn,
      };
    }

    case 'windrose': {
      const theta =
        context.options?.windroseColumns?.theta ??
        DEFAULT_WINDROSE_COLUMNS.theta;
      const r =
        context.options?.windroseColumns?.r ?? DEFAULT_WINDROSE_COLUMNS.r;
      const value =
        context.options?.windroseColumns?.value ??
        DEFAULT_WINDROSE_COLUMNS.value;

      return {
        theta,
        r,
        value,
      };
    }

    default:
      return {};
  }
}

function buildDimensionMappings(
  context: VariableMappingContext
): Record<string, string> {
  return {
    ...buildDefaultDimensionMappings(context),
    ...(context.options?.additionalDimensionMappings ?? {}),
  };
}

function resolveVariableColumn(
  variable: ThresholdVariableModel,
  mappings: Record<string, string>
): string | null {
  const dimensionCandidate = mappings[normalizeKey(variable.dimensionRef)];

  if (dimensionCandidate) {
    return dimensionCandidate;
  }

  const nameCandidate = mappings[normalizeKey(variable.variableName)];

  if (nameCandidate) {
    return nameCandidate;
  }

  if (variable.role === 'time') {
    return mappings.time ?? mappings.t ?? null;
  }

  if (variable.role === 'dependent') {
    return mappings.value ?? mappings.y ?? null;
  }

  if (variable.role === 'independent') {
    return mappings.x ?? mappings.theta ?? mappings.r ?? null;
  }

  return null;
}

function buildVariableMapping(
  context: VariableMappingContext
): Record<string, string> {
  const variableOverrides =
    context.options?.variableOverrides?.[context.threshold.id] ?? {};
  const dimensionMappings = buildDimensionMappings(context);

  return getThresholdVariables(context.threshold).reduce<
    Record<string, string>
  >((accumulator, variable) => {
    const explicitOverride = variableOverrides[variable.variableName];

    if (explicitOverride) {
      return {
        ...accumulator,
        [variable.variableName]: explicitOverride,
      };
    }

    const resolvedColumn = resolveVariableColumn(variable, dimensionMappings);

    if (resolvedColumn) {
      return {
        ...accumulator,
        [variable.variableName]: resolvedColumn,
      };
    }

    if (variable.required) {
      throw new Error(
        `Unable to resolve required variable ${variable.variableName} for threshold ${context.threshold.id}`
      );
    }

    return accumulator;
  }, {});
}

function buildRequestOptions(
  thresholdId: number,
  options?: ThresholdDataQueryBuilderOptions
): ApplyThresholdOptions | undefined {
  const mergedOptions: ApplyThresholdOptions = {
    ...(options?.extraRequestOptions ?? {}),
    ...(options?.perThresholdOptions?.[thresholdId] ?? {}),
  };

  if (typeof options?.returnBreachPointsLimit === 'number') {
    mergedOptions.return_breach_points_limit = options.returnBreachPointsLimit;
  }

  return Object.keys(mergedOptions).length > 0 ? mergedOptions : undefined;
}

function buildRequestDomains(
  thresholdId: number,
  options?: ThresholdDataQueryBuilderOptions
): Record<string, ThresholdDomainParams> | undefined {
  const mergedDomains: Record<string, ThresholdDomainParams> = {
    ...(options?.extraRequestDomains ?? {}),
    ...(options?.perThresholdDomains?.[thresholdId] ?? {}),
  };

  return Object.keys(mergedDomains).length > 0 ? mergedDomains : undefined;
}

/**
 * Builds the additional payload expected by the data endpoint when selected
 * thresholds must be evaluated together with the widget data request.
 *
 * The function is intentionally broader than the strict needs of the current
 * widgets so future threshold families can reuse the same entry point with
 * explicit overrides and extra per-request options.
 *
 * @param input Selected thresholds, selected sources, widget plot type, and
 * extensible builder options.
 * @returns The apply_thresholds payload for the data endpoint.
 *
 * @example
 * const payload = buildThresholdDataQueryParameters({
 *   selectedThresholds,
 *   selectedSources,
 *   plotType: 'windrose',
 *   options: { returnBreachPointsLimit: 500 },
 * });
 */
export function buildThresholdDataQueryParameters(
  input: ThresholdDataQueryBuilderInput
): ThresholdDataQueryParameters {
  if (!isSupportedRequestPlotType(input.plotType)) {
    throw new Error(
      `Unsupported plot type for threshold data request builder: ${input.plotType}`
    );
  }

  if (input.plotType === 'bar') {
    throw new Error('Bar widgets are currently not supported for thresholds');
  }

  input.selectedSources.forEach((source) => assertValidSourceBinding(source));

  const thresholdRequests = input.selectedThresholds.map((threshold) => {
    const variables = buildVariableMapping({
      threshold,
      selectedSources: input.selectedSources,
      plotType: input.plotType,
      options: input.options,
    });
    const requestOptions = buildRequestOptions(threshold.id, input.options);
    const requestDomains = buildRequestDomains(threshold.id, input.options);

    return {
      threshold_scope_assignment_id: threshold.id,
      variables,
      ...(requestDomains ? { domains: requestDomains } : {}),
      ...(requestOptions ? { options: requestOptions } : {}),
    } satisfies ThresholdRequestPayload;
  });

  return {
    function: 'apply_thresholds',
    threshold_requests: thresholdRequests,
  };
}

export default buildThresholdDataQueryParameters;
